import type { LineStringFeature } from "../../../../../utils/models/LineStringFeature";
import type { UndoCommand, CommandContext } from "../types";

export interface RouteEndpointChange {
  routeKey: string;
  /** Index of the moved marker within the route */
  markerIndex: number;
  oldPosition: number[];
  newPosition: number[];
  /** Index of the re-routed linestring within the route */
  lineStringIndex: number;
  oldLineString: LineStringFeature | null;
  newLineString: LineStringFeature | null;
}

/**
 * Command for dragging a stage connector — a single handle that moves the
 * previous route's END marker and the next route's START marker together,
 * re-routing one segment in each route. One undo reverses both routes.
 */
export class MoveConnectorCommand implements UndoCommand {
  readonly type: string = 'MOVE_CONNECTOR';
  // Cross-route operation — no single primary route
  readonly routeKey: string | null = null;

  private changes: RouteEndpointChange[];

  constructor(changes: RouteEndpointChange[]) {
    this.changes = JSON.parse(JSON.stringify(changes));
  }

  undo(ctx: CommandContext): void {
    this.apply(ctx, 'old');
  }

  redo(ctx: CommandContext): void {
    this.apply(ctx, 'new');
  }

  private apply(ctx: CommandContext, direction: 'old' | 'new'): void {
    for (const change of this.changes) {
      const route = ctx.state.routes[change.routeKey];
      if (!route) continue;

      const marker = route.markers[change.markerIndex];
      if (marker) {
        const position = direction === 'old' ? change.oldPosition : change.newPosition;
        marker.markerGeometry.coordinates = [...position];
      }

      const lineString = direction === 'old' ? change.oldLineString : change.newLineString;
      if (lineString && change.lineStringIndex >= 0 && change.lineStringIndex < route.lineStrings.length) {
        route.lineStrings[change.lineStringIndex] = JSON.parse(JSON.stringify(lineString));
      }

      route.updated_at = new Date();
    }
    ctx.state._statsVersion++;
  }
}
